import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns/format";
import { Ban } from "lucide-react";
import { useState } from "react";
import { EllipsisText, ReplyStatusBadge } from "@/components/common";
import ReloadButton from "@/components/common/reload-button";
import { AutoPagination, DataTable, useServerTable } from "@/components/table";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  SearchBar,
  Tabs,
  TabsList,
  TabsTrigger,
  Typography,
} from "@/components/ui";
import useIsMobile from "@/components/ui/sidebar/hooks/use-is-mobile";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import ExportPurchaseOrderDialog from "@/features/factory/components/export-purchase-order-dialog";
import FactoryActionDropdown from "@/features/factory/components/factory-action-dropdown";
import FactoryCheckShipmentRequestsDialog from "@/features/factory/components/factory-check-shipment-requests-dialog";
import FactoryDeliveryDateHistoryDialog from "@/features/factory/components/factory-delivery-date-history-dialog";
import FactoryOrderFilterDialog from "@/features/factory/components/factory-order-filter-dialog";
import { useErpUpdateDate } from "@/features/factory/hooks/use-erp-update-date";
import useFactoryPurchaseOrders from "@/features/factory/hooks/use-factory-purchase-orders";
import type {
  PurchaseOrder,
  RecordStatus,
  SupplierReplyStatus,
  WarningStatus,
} from "@/types/factory.type";
import { formatNumberWithCommas } from "@/utils/format-number";

type TabTypes = "all" | "expired" | "pending" | "replied";

export const Route = createFileRoute("/_protected/factory/home")({
  component: RouteComponent,
});

const getFilterCustomPayload = (tab: TabTypes) => {
  return {
    all: {},
    expired: { warningStatus: "expired" },
    pending: { supplierReplyStatus: "pending,rejected" },
    replied: { supplierReplyStatus: "replied" },
    applied: { shipmentRequestStatus: "applied" },
  }[tab];
};

const isHighlightRow = ({ warningStatus }: PurchaseOrder) => warningStatus === "expired";
const getRowId = (row: PurchaseOrder) => row.id;

const columns: ColumnDef<PurchaseOrder>[] = [
  {
    accessorKey: "poNo",
    header: "採購單號",
    cell: ({ row, getValue }) => {
      const recordStatus: RecordStatus = row.original.recordStatus;
      const isNew = recordStatus === "new";
      const poNo = getValue<string>();
      return (
        <Typography className="flex item-center gap-2">
          {poNo}
          {isNew && (
            <Badge variant="info" className="px-1">
              New
            </Badge>
          )}
        </Typography>
      );
    },
  },
  {
    accessorKey: "supplierName",
    header: "供應商全名",
    cell: ({ getValue }) => {
      const supplierName = getValue<string>();
      return <EllipsisText maxWidth={200}>{supplierName}</EllipsisText>;
    },
  },
  {
    accessorKey: "supplierShortName",
    header: "供應商簡稱",
  },
  {
    accessorKey: "vendorCode",
    header: "供應商代號",
  },
  {
    accessorKey: "buyer",
    header: "採購員",
  },
  {
    accessorKey: "itemNo",
    header: "項次",
  },
  {
    accessorKey: "productNo",
    header: "料號",
  },
  {
    accessorKey: "productName",
    header: "品名",
    size: 250,
  },
  {
    accessorKey: "requiredDate",
    header: "需求交期",
    cell: ({ row }) => (
      <Typography variant="mono">
        {format(new Date(row.getValue("requiredDate")), "yyyy-MM-dd")}
      </Typography>
    ),
  },
  {
    accessorKey: "confirmedDeliveryDate",
    header: "回覆交期",
    cell: ({ getValue, row }) => {
      const confirmedDeliveryDate = getValue<string>();

      if (!confirmedDeliveryDate) return null;

      return <FactoryDeliveryDateHistoryDialog data={row.original} />;
    },
  },
  {
    accessorKey: "deliveryDateRecordCount",
    header: "回覆次數",
  },
  {
    accessorKey: "orderQty",
    header: "需求數量",
    cell: ({ getValue }) => {
      const orderQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(orderQty)}</Typography>;
    },
  },
  {
    accessorKey: "receivedQty",
    header: "已交量",
    cell: ({ getValue }) => {
      const receivedQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(receivedQty)}</Typography>;
    },
  },
  {
    accessorKey: "openQty",
    header: "未交量",
    cell: ({ getValue }) => {
      const openQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(openQty)}</Typography>;
    },
  },
  {
    accessorKey: "note",
    header: "備註",
    cell: ({ getValue }) => {
      const note = getValue<string>();
      return <EllipsisText maxWidth={200}>{note}</EllipsisText>;
    },
  },
  {
    accessorKey: "supplierReplyStatus",
    header: "回覆狀態",
    cell: ({ getValue }) => {
      const status = getValue<SupplierReplyStatus>();
      return <ReplyStatusBadge status={status} />;
    },
  },
  {
    accessorKey: "warningStatus",
    header: "警示狀態",
    cell: ({ getValue }) => {
      const warningStatus = getValue<WarningStatus>();
      if (!warningStatus) return null;
      return <Badge variant="destructive">交期不及</Badge>;
    },
  },
  {
    accessorKey: "isLocked",
    header: "禁止交貨",
    cell: ({ getValue }) => {
      const isLocked = getValue<boolean>();
      return isLocked ? <Ban className="text-destructive h-5 w-5" /> : null;
    },
  },
  {
    id: "shipmentRequest",
    header: "交貨申請狀態",
    cell: ({ row }) => {
      return <FactoryCheckShipmentRequestsDialog order={row.original} />;
    },
  },
];

function RouteComponent() {
  const [tab, setTab] = useState<TabTypes>("all");

  const isMobile = useIsMobile();

  const uiConfig = useAuthStore.use.uiConfig();
  const useShipmentRequestFunction = uiConfig.useShipmentRequestFunction;
  const isSinox = uiConfig.isSinox;

  const { data: erpUpdateDate } = useErpUpdateDate();

  const {
    table,
    isFetching,
    globalFilter,
    setGlobalFilter,
    setSpecificFilter,
    onPaginationChange,
    refetch,
  } = useServerTable<PurchaseOrder>({
    queryFn: useFactoryPurchaseOrders,
    customQueryPayload: getFilterCustomPayload(tab),
    initialPageSize: 20,
    columns,
    options: {
      highlightRow: isHighlightRow,
      selectionOptions: true,
      visibilityOptions: {
        visibilityState: {
          isLocked: useShipmentRequestFunction,
          shipmentRequest: useShipmentRequestFunction,
        },
      },
      getRowId,
    },
  });

  const selectedOrders = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
  const clearSelectedOrders = () => table.resetRowSelection();

  return (
    <section>
      <Card>
        <CardHeader>
          <title>中心廠管理頁面｜Supplier Links</title>
          <Typography variant="h2">採購單列表</Typography>
          <CardDescription>
            <Badge variant="outline">最近資料更新時間：{erpUpdateDate}</Badge>
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col space-y-4">
          <div className="flex justify-between flex-wrap gap-y-4">
            <div className="flex items-center gap-2 order-2 md:order-1 w-full md:w-auto">
              <div className="flex flex-wrap items-center gap-2 w-full">
                <span className="w-full md:w-auto">
                  <SearchBar
                    className="grow"
                    placeholder="請輸入採購單號、供應商、料號或品名..."
                    value={globalFilter}
                    onChange={setGlobalFilter}
                  />
                </span>

                <FactoryActionDropdown selectedOrders={selectedOrders} />
              </div>
            </div>

            <div className="flex items-center gap-2 order-1 md:order-2 w-full md:w-auto">
              <div className="flex flex-wrap items-center gap-2 w-full">
                <div className="order-1 md:order-1 flex-shrink-0">
                  <FactoryOrderFilterDialog table={table} onApply={setSpecificFilter} />
                </div>

                {isSinox && (
                  <div className="order-2 md:order-2 flex-shrink-0 mr-1">
                    <ExportPurchaseOrderDialog />
                  </div>
                )}

                <div className="order-4 md:order-3 w-full sm:flex-1 min-w-0">
                  <Tabs
                    value={tab}
                    onValueChange={(value) => {
                      setTab(value as typeof tab);
                      clearSelectedOrders();
                    }}
                    className="overflow-x-auto"
                  >
                    <TabsList className="min-w-max flex-nowrap">
                      <TabsTrigger value="all">全部</TabsTrigger>
                      <TabsTrigger value="expired">交期不及</TabsTrigger>
                      <TabsTrigger value="pending">未回覆</TabsTrigger>
                      <TabsTrigger value="replied">已回覆（待確認）</TabsTrigger>
                      {useShipmentRequestFunction && (
                        <TabsTrigger value="applied">已申請交貨</TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>
                </div>

                <div className="order-3 md:order-4 flex-shrink-0">
                  <ReloadButton onClick={refetch} />
                </div>
              </div>
            </div>
          </div>

          <DataTable table={table} isFetching={isFetching} />

          <AutoPagination<PurchaseOrder>
            table={table}
            showTotalCount
            showSizeChanger={!isMobile}
            onPaginationChange={onPaginationChange}
          />
        </CardContent>
      </Card>
    </section>
  );
}
